import {handleSessionExpired} from '../modules/auth/core/session'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

// A 401 on an authenticated request means our token is no longer accepted
// (expired/revoked) — kick off the shared session-expired flow. Skipped for the
// login call itself, where a 401 just means bad credentials.
function handleUnauthorized(endpoint: string, status: number) {
  if (status === 401 && authToken && !endpoint.startsWith('/auth/')) {
    handleSessionExpired()
  }
}

let authToken: string | null = localStorage.getItem('admin_token')

export const setToken = (token: string) => {
  authToken = token
  localStorage.setItem('admin_token', token)
}

export const clearToken = () => {
  authToken = null
  localStorage.removeItem('admin_token')
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(authToken ? {Authorization: `Bearer ${authToken}`} : {}),
    ...(options.headers as Record<string, string> ?? {}),
  }
  const res = await fetch(`${BASE_URL}${endpoint}`, {...options, headers})
  if (!res.ok) {
    handleUnauthorized(endpoint, res.status)
    const err = await res.json().catch(() => ({}))
    throw new ApiError(res.status, err?.message ?? `HTTP ${res.status}`)
  }
  // No body to parse: 204 No Content, or an endpoint that returns null/empty
  // (e.g. GET /applications/me before an application exists). Reading the text
  // first avoids "Unexpected end of JSON input" on an empty 200 response.
  if (res.status === 204) return undefined as T
  const text = await res.text()
  return (text ? JSON.parse(text) : undefined) as T
}

// Multipart upload helper — lets the browser set the boundary, so no
// Content-Type header here (unlike request()).
async function upload<T>(endpoint: string, form: FormData): Promise<T> {
  const res = await fetch(`${BASE_URL}${endpoint}`, {
    method: 'POST',
    headers: authToken ? {Authorization: `Bearer ${authToken}`} : {},
    body: form,
  })
  if (!res.ok) {
    handleUnauthorized(endpoint, res.status)
    const err = await res.json().catch(() => ({}))
    throw new ApiError(res.status, err?.message ?? `HTTP ${res.status}`)
  }
  return res.json()
}

// Shape returned by /auth/* and /users/* — mirrors the backend SafeUser.
export interface AuthUser {
  id: string
  email: string
  firstName: string
  lastName: string
  phone: string | null
  role: 'ADMIN' | 'RENTER' | 'CLEANER'
  accountStatus: 'ACTIVE' | 'DISABLED'
  createdAt: string
}

export type ApplicationStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

// Mirrors backend ApplicationView.
export interface Application {
  id: string
  status: ApplicationStatus
  airline: string
  employeeId: string
  badgeKey: string
  idKey: string | null
  emergencyContactName: string
  emergencyContactPhone: string
  rejectionReason: string | null
  reviewedAt: string | null
  createdAt: string
  updatedAt: string
  applicant?: AuthUser
}

export interface SubmitApplicationInput {
  airline: string
  employeeId: string
  /** The renter's own number — stored on their account, not on the application. */
  phone: string
  badgeKey: string
  idKey: string
  emergencyContactName: string
  emergencyContactPhone: string
}

export interface AuditLogRow {
  id: string
  actorEmail: string | null
  actorRole: string | null
  action: string
  entityId: string | null
  method: string
  path: string
  statusCode: number | null
  ip: string | null
  details: Record<string, unknown> | null
  createdAt: string
}

export type EmailStatus = 'SENT' | 'FAILED' | 'DELIVERED' | 'BOUNCED' | 'COMPLAINED'

export interface EmailLogRow {
  id: string
  toAddress: string
  subject: string
  status: EmailStatus
  reason: string | null
  attachmentCount: number
  createdAt: string
  updatedAt: string
}

export interface AuditPage {
  rows: AuditLogRow[]
  total: number
  page: number
  pageSize: number
}

export interface AuditQuery {
  search?: string
  action?: string
  actorEmail?: string
  ip?: string
  from?: string
  to?: string
  page?: number
  pageSize?: number
}

export interface StoredFile {
  key: string
  contentType: string
  size: number
}

// ─── Module 7: cleaning & maintenance requests ──────────────────────────
export type RequestTypeValue = 'CLEANING' | 'MAINTENANCE' | 'SUGGESTION'
export type RequestStatusValue = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED'

export interface RequestUpdate {
  at: string
  status: RequestStatusValue
  note: string | null
}

export interface MaintenanceRequest {
  id: string
  type: RequestTypeValue
  status: RequestStatusValue
  title: string
  description: string
  attachmentKey: string | null
  prepaid: boolean
  adminNote: string | null
  updates: RequestUpdate[]
  bed: {id: string; code: string} | null
  resolvedAt: string | null
  createdAt: string
  updatedAt: string
  renter?: AuthUser
}

export interface CreateRequestInput {
  type: RequestTypeValue
  title: string
  description: string
  attachmentKey?: string
}

export interface ChatMessage {
  id: string
  authorRole: 'RENTER' | 'ADMIN'
  authorName: string
  body: string
  createdAt: string
}

export type BedStatusValue = 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'OUT_OF_SERVICE'
export type BedTypeValue = 'TWIN' | 'FULL' | 'QUEEN'

export interface BedRoomRef {
  id: string
  slug: string
  name: string
}

export interface Bed {
  id: string
  code: string
  type: BedTypeValue
  status: BedStatusValue
  level: string | null
  photo: string | null
  keyFob: string | null
  boardingPass: string | null
  pricePerMonth: string
  room: BedRoomRef | null
  occupant: AuthUser | null
}

export type PublicBedStatus = 'AVAILABLE' | 'RESERVED' | 'OCCUPIED' | 'OUT_OF_SERVICE'

/** A tile in the public "Inside the crashpad" gallery. */
export interface GalleryItem {
  image: string
  label: string
}

/** Approval welcome-email template + attachments (admin-editable). */
export interface WelcomeLink {
  label: string
  url: string
}

export interface WelcomeAttachment {
  key: string
  filename: string
  /** Label shown on the download button in the packet email. */
  title?: string
}
export interface WelcomeEmail {
  enabled: boolean
  subject: string
  heading: string
  body: string
  attachments: WelcomeAttachment[]
  links: WelcomeLink[]
  updatedAt: string
}
export type WelcomeEmailInput = Partial<Omit<WelcomeEmail, 'updatedAt'>>

/** An editable renter-journey email (account welcome / application / approval). */
export interface EmailTemplate {
  key: 'account_welcome' | 'application_received' | 'booking_approved' | 'deposit_received'
  order: number
  title: string
  description: string
  placeholders: string[]
  enabled: boolean
  subject: string
  heading: string
  body: string
  updatedAt: string
}
export type EmailTemplateInput = Partial<Pick<EmailTemplate, 'enabled' | 'subject' | 'heading' | 'body'>>

/** Public per-room bed availability (no occupant PII). */
export interface RoomBed {
  id: string
  code: string
  type: BedTypeValue
  level: string | null
  photo: string | null
  pricePerMonth: string
  available: boolean
  status: PublicBedStatus
}

export interface OccupancySummary {
  total: number
  available: number
  occupied: number
  reserved: number
  outOfService: number
}

export type BookingStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'WAITLISTED' | 'CANCELLED' | 'COMPLETED'

export interface AvailableBed {
  id: string
  code: string
  type: BedTypeValue
  status: BedStatusValue
}

export interface BookingPayment {
  id: string
  receiptNumber: string
  amount: string
  method: PaymentMethodValue
  status: PaymentStatusValue
  periodLabel: string | null
  note: string | null
  paidAt: string | null
  createdAt: string
}

/** A renter's still-held deposit on a prior booking (for carry-over). */
export interface TransferableDeposit {
  bookingId: string
  amount: string
  receiptNumber: string
  bedCode: string | null
}

export interface Booking {
  id: string
  status: BookingStatus
  startDate: string
  endDate: string | null
  unitPrice: string | null
  months: number | null
  amount: string | null
  paid: boolean
  /** Rent paid toward the stay total — excludes the refundable deposit. */
  paidAmount: string
  /** Refundable deposit currently held (PAID and not yet refunded). */
  depositHeld: string
  /** Monthly rent subscription state. */
  subscriptionStatus: string | null
  hasSubscription: boolean
  /** App-managed rent (saved card, we bill it) is switched on. */
  rentActive: boolean
  /** A reusable card is saved (from the deposit) — rent can start with no re-entry. */
  cardOnFile: boolean
  /** Saved card brand + last4 for display (null when no card on file). */
  cardBrand: string | null
  cardLast4: string | null
  /** When our sweep next charges the saved card (app-managed rent). */
  nextRentChargeAt: string | null
  moveOutDate: string | null
  accessUntil: string | null
  depositTransferredToId: string | null
  depositTransferredFromId: string | null
  checkedInAt: string | null
  checkedOutAt: string | null
  departureConfirmedAt: string | null
  reviewedAt: string | null
  note: string | null
  rejectionReason: string | null
  createdAt: string
  updatedAt: string
  bed: {id: string; code: string; type: string; status: string} | null
  payments: BookingPayment[]
  applicant?: AuthUser
}

export type PaymentStatusValue = 'PENDING' | 'PAID' | 'OVERDUE' | 'REFUNDED'
export type PaymentMethodValue = 'ZELLE' | 'CASH' | 'CARD' | 'OTHER'

export interface Payment {
  id: string
  receiptNumber: string
  amount: string
  method: PaymentMethodValue
  status: PaymentStatusValue
  periodLabel: string | null
  reference: string | null
  note: string | null
  paidAt: string | null
  createdAt: string
  bookingId: string | null
  renter?: AuthUser
  bed: {id: string; code: string; type: string} | null
  /** Full Stripe gateway response for card payments (admin-facing); null otherwise. */
  stripeResponse: Record<string, unknown> | null
}

export interface BedWash {
  id: string
  bedCode: string | null
  status: 'QUEUED' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'
  source: 'REQUESTED' | 'AUTO'
  amount: string
  renter: AuthUser | null
  completedAt: string | null
  createdAt: string
}

/** A bed's next scheduled (not-yet-due) automatic wash. */
export interface UpcomingWash {
  bedCode: string | null
  renterName: string | null
  nextWashAt: string
}

/** A line on the cleaner's earnings tab (bed wash or cleaning visit). */
export interface CleanerEarning {
  id: string
  kind: 'BED_WASH' | 'CLEANING_VISIT'
  amount: string
  status: 'PENDING_APPROVAL' | 'OWED' | 'PAID' | 'DECLINED'
  note: string | null
  occurredOn: string
  bedCode: string | null
  cleaner: AuthUser | null
  approvedAt: string | null
  createdAt: string
}

export interface CleanerPayout {
  id: string
  amount: string
  method: 'STRIPE' | 'MANUAL'
  status: 'PENDING' | 'PAID' | 'FAILED'
  note: string | null
  paidAt: string | null
  createdAt: string
}

/** Summary of what's currently owed to the cleaner. */
export interface CleanerTab {
  cleanerId: string | null
  owed: string
  owedCount: number
  pendingCount: number
  baseRate: string
  threshold: string
}

export interface PaymentInstructions {
  zelleHandle: string
  instructions: string
}

/** The single cleaner login + payout-onboarding state, for the admin Cleaner tab. */
export interface CleanerAccount {
  cleaner: AuthUser | null
  connectStatus: 'NONE' | 'PENDING' | 'VERIFIED'
}

/** Safe, non-sensitive summary of her Stripe payout account. */
export interface ConnectSummary {
  payoutsEnabled: boolean
  detailsSubmitted: boolean
  bankName: string | null
  bankLast4: string | null
  routingNumber: string | null
  accountHolderName: string | null
  dashboardUrl: string | null
}

export interface CreateCleanerInput {
  email: string
  firstName: string
  lastName: string
  phone?: string | null
  password: string
}

export type UpdateCleanerInput = {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string | null
}

/** Renter-safe: which rails are enabled + the deposit amount. No secrets. */
export interface PaymentMethods {
  depositAmount: string
  rentIntervalValue: number
  rentIntervalUnit: string
  bedWashFee: string
  bedWashIntervalValue: number
  bedWashIntervalUnit: string
  zelle: {enabled: boolean; name: string; handle: string; instructions: string | null}
  stripe: {enabled: boolean; publishableKey: string | null}
}

/** "each month" / "every 2 minutes" — a billing cadence for renter-facing copy. */
export const formatCadence = (value: number, unit: string): string =>
  value === 1 ? `each ${unit}` : `every ${value} ${unit}s`

/** "every 6 weeks" / "every week" — an interval phrase for renter-facing copy. */
export const formatEvery = (value: number, unit: string): string =>
  value === 1 ? `every ${unit}` : `every ${value} ${unit}s`

/** Full admin payment settings (includes Stripe secrets — admin only). */
export interface PaymentSettings {
  zelleEnabled: boolean
  zelleName: string
  zelleHandle: string
  zelleInstructions: string | null
  stripeEnabled: boolean
  stripePublishableKey: string | null
  stripeSecretKey: string | null
  stripeWebhookSecret: string | null
  notifyEmail: string | null
  depositAmount: string
  rentAutoEnabled: boolean
  rentIntervalValue: number
  rentIntervalUnit: string
  bedWashAutoEnabled: boolean
  bedWashIntervalValue: number
  bedWashIntervalUnit: string
  bedWashFee: string
  cleanerPayoutThreshold: string
  cleanerAutoPayoutEnabled: boolean
  /** Env flag — whether the Stripe config section shows in the admin panel. */
  stripeAdminEnabled: boolean
  /** Env flag (ZELLE_ENABLED) — whether Zelle exists at all on this install. */
  zelleAdminEnabled: boolean
  updatedAt: string
}

export type PaymentSettingsInput = Partial<Omit<PaymentSettings, 'updatedAt' | 'stripeAdminEnabled' | 'zelleAdminEnabled'>>

export interface RecordPaymentInput {
  userId: string
  bedId?: string
  bookingId?: string
  amount: string
  method: PaymentMethodValue
  status?: PaymentStatusValue
  periodLabel?: string
  reference?: string
  note?: string
  paidAt?: string
}

// ─── Module 11: listings (admin-managed Rooms & Plans) ──────────────────
export interface PriceOption {
  label: string
  value: string
}

export interface ListingRule {
  allowed: boolean
  text: string
}

export interface Listing {
  id: string
  slug: string
  name: string
  title: string
  cadence: string
  price: string
  priceUnit: string
  beds: string
  baths: string
  guests: string
  blurb: string
  features: string[]
  description: string
  bedrooms: number
  bedsCount: number
  size: string
  type: string
  address: string | null
  priceOptions: PriceOption[]
  deposit: string
  detailAmenities: string[]
  rules: ListingRule[]
  gallery: string[]
  published: boolean
  sortOrder: number
  /** The room's single bed type (source of type + monthly price). */
  bedType: {id: string; key: BedTypeValue; label: string; pricePerMonth: string} | null
  fromPrice: string | null
}

/** The global bed-type price catalog (TWIN/FULL/QUEEN). */
export interface BedTypeCatalog {
  id: string
  key: BedTypeValue
  label: string
  pricePerMonth: string
  sortOrder: number
}

export type ListingInput = Omit<Listing, 'id' | 'published' | 'sortOrder' | 'fromPrice' | 'bedType'> &
  Partial<Pick<Listing, 'published' | 'sortOrder'>> & {bedTypeId?: string}

/**
 * Resolve a gallery entry to a usable <img> src. Bundled paths (/images/…) and
 * absolute URLs pass through; anything else is an uploaded storage key served
 * via the public image passthrough.
 */
export const listingImageSrc = (src: string): string => {
  if (!src) return ''
  if (src.startsWith('/') || src.startsWith('http')) return src
  return `${BASE_URL}/listings/image/${src}`
}

// ─── Module 8: announcements & door codes ───────────────────────────────
export interface Announcement {
  id: string
  title: string
  body: string
  pinned: boolean
  published: boolean
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AnnouncementInput {
  title: string
  body: string
  pinned?: boolean
  published?: boolean
}

export interface DoorCode {
  id: string
  label: string
  code: string
  note: string | null
  sortOrder: number
  updatedAt: string
}

export interface DoorCodeInput {
  label: string
  code: string
  note?: string
  sortOrder?: number
}

// ─── Module 9: info pages ────────────────────────────────────────────────
export interface InfoPage {
  id: string
  slug: string
  title: string
  body: string
  sortOrder: number
  updatedAt: string
}

// ─── Dashboard stats ─────────────────────────────────────────────────────
export interface DashboardStats {
  counts: {
    renters: number
    activeRenters: number
    verifiedRenters: number
    beds: number
    occupiedBeds: number
    openRequests: number
    pendingApplications: number
    pendingBookings: number
  }
  occupancy: Record<BedStatusValue, number>
  bookingsByStatus: Record<BookingStatus, number>
  applicationsByStatus: Record<ApplicationStatus, number>
  requestsByStatus: Record<RequestStatusValue, number>
  revenue: {total: number; byMonth: {month: string; amount: number}[]}
  bookingsByMonth: {month: string; count: number}[]
}

// ─── Flyer automation (Module 10) ─────────────────────────────────────
export interface FlyerBedOption {
  type: 'TWIN' | 'FULL' | 'QUEEN'
  label: string
  price: string
}

export interface FlyerContent {
  airportCode: string
  city: string
  badges: string[]
  priceSuffix: string
  bedOptions: FlyerBedOption[]
  depositAmount: string
  amenities: string[]
  transitTitle: string
  transit: string[]
  amenitiesExtra: string[]
  footerText: string
  heroImage: string | null
  keysImage: string | null
  photos: string[]
}

export interface FlyerSettings {
  headline: string
  subtitle: string
  phone: string | null
  email: string | null
  ctaLabel: string
  ctaUrl: string
  content: FlyerContent
  updatedAt: string
}

/** Editable fields (phone/email accept '' to clear). */
export interface FlyerSettingsInput {
  headline: string
  subtitle: string
  phone: string
  email: string
  ctaLabel: string
  ctaUrl: string
  content: FlyerContent
}

export interface FlyerRoom {
  id: string
  slug: string
  name: string
  type: string
  priceUnit: string
  totalBeds: number
  availableBeds: number
  fromMonthly: number | null
}

export interface FlyerData {
  settings: FlyerSettings
  rooms: FlyerRoom[]
  byType: Record<string, number>
  totals: {availableBeds: number; totalBeds: number}
  generatedAt: string
}

export const api = {
  // Auth — single login for both roles; the returned user carries the role.
  login: (email: string, password: string) =>
    request<{accessToken: string; user: AuthUser}>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({email, password}),
    }),

  register: (data: {email: string; password: string; firstName: string; lastName: string}) =>
    request<{accessToken: string; user: AuthUser}>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getMe: () => request<AuthUser>('/auth/me'),

  forgotPassword: (email: string) =>
    request<void>('/auth/forgot-password', {method: 'POST', body: JSON.stringify({email})}),

  resetPassword: (token: string, newPassword: string) =>
    request<void>('/auth/reset-password', {method: 'POST', body: JSON.stringify({token, newPassword})}),

  // ─── Module 2: admin account management ─────────────────────────────
  listUsers: (role?: 'ADMIN' | 'RENTER') =>
    request<AuthUser[]>(`/users${role ? `?role=${role}` : ''}`),

  updateUser: (id: string, data: {firstName?: string; lastName?: string; phone?: string | null}) =>
    request<AuthUser>(`/users/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  setUserStatus: (id: string, status: 'ACTIVE' | 'DISABLED') =>
    request<AuthUser>(`/users/${id}/status`, {method: 'PATCH', body: JSON.stringify({status})}),

  // ─── Module 2: renter self-service ──────────────────────────────────
  updateMyProfile: (data: {firstName?: string; lastName?: string; phone?: string | null}) =>
    request<AuthUser>('/users/me', {method: 'PATCH', body: JSON.stringify(data)}),

  changeMyPassword: (currentPassword: string, newPassword: string) =>
    request<void>('/users/me/password', {method: 'PATCH', body: JSON.stringify({currentPassword, newPassword})}),

  // ─── Welcome email (admin) ──────────────────────────────────────────
  getWelcomeEmail: () => request<WelcomeEmail>('/welcome-email'),
  updateWelcomeEmail: (data: WelcomeEmailInput) =>
    request<WelcomeEmail>('/welcome-email', {method: 'PATCH', body: JSON.stringify(data)}),

  getEmailTemplates: () => request<EmailTemplate[]>('/email-templates'),
  updateEmailTemplate: (key: string, data: EmailTemplateInput) =>
    request<EmailTemplate>(`/email-templates/${key}`, {method: 'PATCH', body: JSON.stringify(data)}),

  // ─── Site content: public "Inside the crashpad" gallery ─────────────
  getSiteGallery: () => request<{gallery: GalleryItem[]; updatedAt: string}>('/site/gallery'),
  updateSiteGallery: (gallery: GalleryItem[]) =>
    request<{gallery: GalleryItem[]; updatedAt: string}>('/site/gallery', {method: 'PATCH', body: JSON.stringify({gallery})}),

  // ─── S1: file upload + protected retrieval ──────────────────────────
  uploadFile: (file: File) => {
    const form = new FormData()
    form.append('file', file)
    return upload<StoredFile>('/files', form)
  },

  /** Fetch a protected file (with auth) → blob URL + content type. */
  fileBlobUrl: async (key: string): Promise<{url: string; type: string}> => {
    const res = await fetch(`${BASE_URL}/files/${key}`, {
      headers: authToken ? {Authorization: `Bearer ${authToken}`} : {},
    })
    if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`)
    const blob = await res.blob()
    return {url: URL.createObjectURL(blob), type: blob.type}
  },

  // ─── Module 3: applications ─────────────────────────────────────────
  submitApplication: (data: SubmitApplicationInput) =>
    request<Application>('/applications', {method: 'POST', body: JSON.stringify(data)}),

  getMyApplication: () => request<Application | null>('/applications/me'),

  listApplications: (status?: ApplicationStatus) =>
    request<Application[]>(`/applications${status ? `?status=${status}` : ''}`),

  getApplication: (id: string) => request<Application>(`/applications/${id}`),

  approveApplication: (id: string) =>
    request<Application>(`/applications/${id}/approve`, {method: 'PATCH'}),

  rejectApplication: (id: string, reason?: string) =>
    request<Application>(`/applications/${id}/reject`, {method: 'PATCH', body: JSON.stringify({reason})}),

  // ─── Module 4: beds (admin) ─────────────────────────────────────────
  listBeds: () => request<Bed[]>('/beds'),

  bedsSummary: () => request<OccupancySummary>('/beds/summary'),

  assignBed: (id: string, userId: string) =>
    request<Bed>(`/beds/${id}/assign`, {method: 'PATCH', body: JSON.stringify({userId})}),

  unassignBed: (id: string) =>
    request<Bed>(`/beds/${id}/unassign`, {method: 'PATCH'}),

  setBedStatus: (id: string, status: Exclude<BedStatusValue, 'OCCUPIED'>) =>
    request<Bed>(`/beds/${id}/status`, {method: 'PATCH', body: JSON.stringify({status})}),

  createBed: (data: {bedTypeId: string; roomId?: string; level?: string; code?: string}) =>
    request<Bed>('/beds', {method: 'POST', body: JSON.stringify(data)}),

  updateBed: (id: string, data: {level?: string; photo?: string | null; keyFob?: string | null; boardingPass?: string | null}) =>
    request<Bed>(`/beds/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  deleteBed: (id: string) =>
    request<void>(`/beds/${id}`, {method: 'DELETE'}),

  // ─── Bed-type price catalog ─────────────────────────────────────────
  setRoomBeds: (roomId: string, bedIds: string[]) =>
    request<{ok: true}>(`/listings/${roomId}/beds`, {method: 'PATCH', body: JSON.stringify({bedIds})}),

  bedTypes: () => request<BedTypeCatalog[]>('/bed-types'),
  updateBedType: (id: string, data: {label?: string; pricePerMonth?: string}) =>
    request<BedTypeCatalog>(`/bed-types/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  // ─── Module 5: bookings ─────────────────────────────────────────────
  availableBeds: () => request<AvailableBed[]>('/bookings/available-beds'),

  createBooking: (data: {bedId: string; startDate: string; months?: number; note?: string}) =>
    request<Booking>('/bookings', {method: 'POST', body: JSON.stringify(data)}),

  joinWaitlist: (data: {bedId: string; startDate: string; months?: number; note?: string}) =>
    request<Booking>('/bookings/waitlist', {method: 'POST', body: JSON.stringify(data)}),

  myBookings: () => request<Booking[]>('/bookings/me'),

  cancelBooking: (id: string) =>
    request<Booking>(`/bookings/${id}/cancel`, {method: 'PATCH'}),

  confirmDeparture: (id: string) =>
    request<Booking>(`/bookings/${id}/confirm-departure`, {method: 'PATCH'}),

  listBookings: (status?: BookingStatus) =>
    request<Booking[]>(`/bookings${status ? `?status=${status}` : ''}`),

  getBooking: (id: string) => request<Booking>(`/bookings/${id}`),

  approveBooking: (id: string, depositReceived = false, transferDepositFrom?: string) =>
    request<Booking>(`/bookings/${id}/approve`, {method: 'PATCH', body: JSON.stringify({depositReceived, ...(transferDepositFrom ? {transferDepositFrom} : {})})}),

  /** The renter's still-held deposit on a prior booking (null if none). */
  transferableDeposit: (bookingId: string) =>
    request<TransferableDeposit | null>(`/bookings/${bookingId}/transferable-deposit`),

  /** Public "Contact us" form — acknowledges the sender + forwards to the owner. */
  submitContact: (payload: {name: string; email: string; message: string}) =>
    request<{ok: true}>(`/contact`, {method: 'POST', body: JSON.stringify(payload)}),

  rejectBooking: (id: string, reason?: string) =>
    request<Booking>(`/bookings/${id}/reject`, {method: 'PATCH', body: JSON.stringify({reason})}),

  waitlistBooking: (id: string) =>
    request<Booking>(`/bookings/${id}/waitlist`, {method: 'PATCH'}),

  adminCancelBooking: (id: string) =>
    request<Booking>(`/bookings/${id}/cancel-admin`, {method: 'PATCH'}),

  /** Admin resends the welcome packet (directions + attachments) to the renter. */
  resendWelcomePacket: (id: string) =>
    request<{to: string}>(`/bookings/${id}/resend-welcome`, {method: 'POST'}),

  checkInBooking: (id: string) =>
    request<Booking>(`/bookings/${id}/check-in`, {method: 'PATCH'}),

  checkOutBooking: (id: string) =>
    request<Booking>(`/bookings/${id}/check-out`, {method: 'PATCH'}),

  // ─── Module 6: payments ─────────────────────────────────────────────
  paymentInstructions: () => request<PaymentInstructions>('/payments/instructions'),

  /** Enabled rails (Zelle/Stripe) + deposit amount for the renter pay step. */
  paymentMethods: () => request<PaymentMethods>('/payments/methods'),

  /** Start a Stripe card deposit; returns the hosted Checkout URL to redirect to. */
  startStripeDeposit: (data?: {bookingId?: string; bedId?: string}) =>
    request<{url: string}>('/payments/stripe/deposit-session', {method: 'POST', body: JSON.stringify(data ?? {})}),

  /** Settle a Checkout Session on return from Stripe (before the webhook lands). */
  confirmStripeSession: (sessionId: string) =>
    request<{settled: 'subscription' | 'rent_setup' | 'card_saved' | 'deposit' | 'rent' | 'bedwash' | null}>('/payments/stripe/confirm-session', {method: 'POST', body: JSON.stringify({sessionId})}),

  /** Save a card (setup checkout) to charge later for deposit/rent/bed wash. */
  saveCardSession: (bookingId?: string) =>
    request<{url: string}>('/payments/stripe/save-card-session', {method: 'POST', body: JSON.stringify(bookingId ? {bookingId} : {})}),

  /** Remove the saved card (detach from Stripe + clear from the booking). */
  removeCard: (bookingId?: string) =>
    request<void>('/payments/stripe/remove-card', {method: 'POST', body: JSON.stringify(bookingId ? {bookingId} : {})}),

  /** Re-sync the saved card from Stripe after the renter changes it in the portal. */
  syncCard: (bookingId?: string) =>
    request<{cardOnFile: boolean}>('/payments/stripe/sync-card', {method: 'POST', body: JSON.stringify(bookingId ? {bookingId} : {})}),

  /** Charge the refundable deposit on the saved card off-session (no redirect). */
  chargeDeposit: (bookingId?: string) =>
    request<{receiptNumber: string; amount: string}>('/payments/stripe/charge-deposit', {method: 'POST', body: JSON.stringify(bookingId ? {bookingId} : {})}),

  /** Start a Stripe card payment for the outstanding rent on a booking. */
  startStripeRent: (bookingId: string) =>
    request<{url: string}>('/payments/stripe/rent-session', {method: 'POST', body: JSON.stringify({bookingId})}),

  /** Start app-managed monthly rent. Reuses the card saved at the deposit when
   *  there is one ({activated:true}); otherwise returns a setup-checkout {url}. */
  startRentSubscription: (bookingId: string) =>
    request<{url: string} | {activated: true}>('/payments/stripe/rent-setup-session', {method: 'POST', body: JSON.stringify({bookingId})}),

  /** Charge the next month's rent now against the saved card (pay ahead). */
  chargeRentNow: (bookingId: string) =>
    request<{receiptNumber: string; amount: string}>('/payments/stripe/charge-rent-now', {method: 'POST', body: JSON.stringify({bookingId})}),

  /** Stop app-managed monthly rent — no more charges; access runs through the paid period. */
  cancelRent: (bookingId: string) =>
    request<{rentActive: boolean; accessUntil: string | null}>('/payments/stripe/cancel-rent', {method: 'POST', body: JSON.stringify({bookingId})}),

  /** Start a $5 Stripe Checkout for a renter-requested bed wash. */
  startBedWash: (requestId?: string) =>
    request<{url: string} | {paid: true; receiptNumber: string}>('/payments/stripe/bedwash-session', {method: 'POST', body: JSON.stringify({requestId})}),

  /** When the renter's bed is next due for an automatic wash + charge. */
  bedWashNext: () =>
    request<{autoEnabled: boolean; nextWashAt: string | null}>('/payments/bed-wash/next'),

  /** The current renter's own bed-wash history. */
  myBedWashes: () => request<BedWash[]>('/bed-washes/mine'),

  /** Housekeeping (cleaner/admin): the open wash queue. */
  bedWashQueue: () => request<BedWash[]>('/bed-washes/queue'),
  /** Upcoming (not-yet-due) scheduled washes across all beds. */
  bedWashUpcoming: () =>
    request<UpcomingWash[]>('/payments/bed-wash/upcoming'),

  /** Housekeeping (cleaner/admin): recent wash history. */
  bedWashHistory: () => request<BedWash[]>('/bed-washes'),

  /** Housekeeping (cleaner/admin): mark a wash started. */
  markBedWashInProgress: (id: string) =>
    request<BedWash>(`/bed-washes/${id}/start`, {method: 'PATCH'}),

  /** Housekeeping (cleaner/admin): mark a wash done. */
  markBedWashDone: (id: string) =>
    request<BedWash>(`/bed-washes/${id}/done`, {method: 'PATCH'}),

  // ─── Cleaner earnings (cleaner-facing: her tab + payout history) ─────
  cleanerTab: () => request<CleanerTab>('/cleaner-earnings/tab'),
  myCleanerEarnings: () => request<CleanerEarning[]>('/cleaner-earnings/mine'),
  myCleanerPayouts: () => request<CleanerPayout[]>('/cleaner-earnings/mine/payouts'),

  // ─── Her own Stripe payout onboarding (cleaner-facing) ─────
  myConnectStatus: () =>
    request<{status: 'NONE' | 'PENDING' | 'VERIFIED'}>('/cleaner-earnings/mine/connect'),
  myConnectLink: () =>
    request<{url: string}>('/cleaner-earnings/mine/connect/link', {method: 'POST'}),
  myConnectRefresh: () =>
    request<{status: 'NONE' | 'PENDING' | 'VERIFIED'}>('/cleaner-earnings/mine/connect/refresh', {method: 'POST'}),
  myConnectSummary: () =>
    request<ConnectSummary | null>('/cleaner-earnings/mine/connect/summary'),
  myConnectLoginLink: () =>
    request<{url: string}>('/cleaner-earnings/mine/connect/login-link', {method: 'POST'}),

  /** Cleaner (or admin) logs an apartment-cleaning visit. */
  logCleaningVisit: (data: {amount?: string; occurredOn?: string; note?: string}) =>
    request<CleanerEarning>('/cleaner-earnings/visits', {method: 'POST', body: JSON.stringify(data)}),

  // ─── Cleaner earnings (admin-facing: ledger + approvals) ─────────────
  adminCleanerEarnings: () => request<CleanerEarning[]>('/cleaner-earnings'),
  adminPendingCleanerEarnings: () => request<CleanerEarning[]>('/cleaner-earnings/pending'),
  adminCleanerPayouts: () => request<CleanerPayout[]>('/cleaner-earnings/payouts'),

  approveCleanerEarning: (id: string, amount?: string) =>
    request<CleanerEarning>(`/cleaner-earnings/${id}/approve`, {method: 'PATCH', body: JSON.stringify(amount ? {amount} : {})}),

  declineCleanerEarning: (id: string) =>
    request<CleanerEarning>(`/cleaner-earnings/${id}/decline`, {method: 'PATCH'}),

  /** Admin records a payout paid outside the app (Zelle). Settles the tab. */
  manualCleanerPayout: (note?: string) =>
    request<CleanerPayout | {ok: false; reason: string}>('/cleaner-earnings/payouts/manual', {method: 'POST', body: JSON.stringify(note ? {note} : {})}),

  /** Owed total, onboarding status, and available Stripe balance for the payout panel. */
  cleanerPayoutInfo: () =>
    request<{owed: string; owedCount: number; connectStatus: 'NONE' | 'PENDING' | 'VERIFIED'; availableCents: number | null; canPayStripe: boolean; autoPayoutEnabled: boolean}>('/cleaner-earnings/payout-info'),

  /** Pay the whole owed tab to the cleaner via one Stripe transfer. */
  stripeCleanerPayout: () =>
    request<CleanerPayout>('/cleaner-earnings/payouts/stripe', {method: 'POST'}),

  // ─── Cleaner account management (admin "Cleaner" tab) ────────────────
  getCleanerAccount: () => request<CleanerAccount>('/cleaner-account'),

  createCleaner: (data: CreateCleanerInput) =>
    request<CleanerAccount>('/cleaner-account', {method: 'POST', body: JSON.stringify(data)}),

  updateCleaner: (data: UpdateCleanerInput) =>
    request<CleanerAccount>('/cleaner-account', {method: 'PATCH', body: JSON.stringify(data)}),

  setCleanerPassword: (newPassword: string) =>
    request<{ok: true}>('/cleaner-account/password', {method: 'PATCH', body: JSON.stringify({newPassword})}),

  /** Start/resume the cleaner's Stripe payout onboarding — returns the link to send her. */
  cleanerConnectLink: () =>
    request<{url: string}>('/cleaner-account/connect/link', {method: 'POST'}),

  /** Re-check her Stripe account and return the synced status. */
  cleanerConnectRefresh: () =>
    request<{status: 'NONE' | 'PENDING' | 'VERIFIED'}>('/cleaner-account/connect/refresh', {method: 'POST'}),

  /** Delete her payout account (removed on Stripe too) and reset to NONE. */
  cleanerConnectDelete: () =>
    request<{status: 'NONE' | 'PENDING' | 'VERIFIED'}>('/cleaner-account/connect', {method: 'DELETE'}),

  /** Admin: safe summary of her payout account (bank name + last 4, status). */
  cleanerConnectSummary: () =>
    request<ConnectSummary | null>('/cleaner-account/connect/summary'),

  /** Open the Stripe billing portal (manage card / cancel subscription). */
  openBillingPortal: (bookingId: string) =>
    request<{url: string}>('/payments/stripe/billing-portal', {method: 'POST', body: JSON.stringify({bookingId})}),

  /** Schedule a move-out date; cancels the subscription with a prorated final month. */
  scheduleMoveOut: (bookingId: string, moveOutDate: string) =>
    request<{moveOutDate: string | null; accessUntil: string | null}>('/payments/stripe/schedule-move-out', {method: 'POST', body: JSON.stringify({bookingId, moveOutDate})}),

  /** Admin: full payment settings (Zelle + Stripe, incl. secrets). */
  getPaymentSettings: () => request<PaymentSettings>('/payments/settings'),
  updatePaymentSettings: (data: PaymentSettingsInput) =>
    request<PaymentSettings>('/payments/settings', {method: 'PATCH', body: JSON.stringify(data)}),

  myPayments: () => request<Payment[]>('/payments/me'),

  recordPayment: (data: RecordPaymentInput) =>
    request<Payment>('/payments', {method: 'POST', body: JSON.stringify(data)}),

  listPayments: (params?: {userId?: string; bedId?: string}) => {
    const qs = params ? new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : ''
    return request<Payment[]>(`/payments${qs ? `?${qs}` : ''}`)
  },

  updatePayment: (id: string, data: {status?: PaymentStatusValue; note?: string}) =>
    request<Payment>(`/payments/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  resendReceipt: (id: string) =>
    request<void>(`/payments/${id}/receipt`, {method: 'POST'}),

  // ─── Module 11: listings ────────────────────────────────────────────
  // ─── Audit trail (admin) ────────────────────────────────────────────────
  auditLogs: (q: AuditQuery = {}) => {
    const p = new URLSearchParams()
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') p.set(k, String(v))
    const qs = p.toString()
    return request<AuditPage>(`/audit-logs${qs ? `?${qs}` : ''}`)
  },

  auditActions: () => request<string[]>('/audit-logs/actions'),

  /** Sent emails, newest first — to: any part of the recipient; dateFrom / dateTo: YYYY-MM-DD, both days included. */
  emailLogs: (to?: string, limit = 100, dateFrom?: string, dateTo?: string) => {
    const qs = new URLSearchParams({limit: String(limit)})
    if (to) qs.set('to', to)
    if (dateFrom) qs.set('dateFrom', dateFrom)
    if (dateTo) qs.set('dateTo', dateTo)
    return request<EmailLogRow[]>(`/email-logs?${qs}`)
  },

  publicListings: () => request<Listing[]>('/listings'),

  publicListing: (slug: string) => request<Listing>(`/listings/${slug}`),

  roomBeds: (slug: string) => request<RoomBed[]>(`/listings/${slug}/beds`),

  adminListings: () => request<Listing[]>('/listings/admin/all'),

  createListing: (data: ListingInput) =>
    request<Listing>('/listings', {method: 'POST', body: JSON.stringify(data)}),

  updateListing: (id: string, data: Partial<ListingInput>) =>
    request<Listing>(`/listings/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  deleteListing: (id: string) =>
    request<void>(`/listings/${id}`, {method: 'DELETE'}),

  // ─── Module 7: requests ─────────────────────────────────────────────
  createRequest: (data: CreateRequestInput) =>
    request<MaintenanceRequest>('/requests', {method: 'POST', body: JSON.stringify(data)}),

  myRequests: () => request<MaintenanceRequest[]>('/requests/me'),

  /** Log a paid bed-sheet-washing request (prepaid — not cancellable). */
  logBedWashRequest: (fee: string) =>
    request<MaintenanceRequest>('/requests/bed-wash-log', {method: 'POST', body: JSON.stringify({fee})}),

  cancelRequest: (id: string) =>
    request<MaintenanceRequest>(`/requests/${id}/cancel`, {method: 'PATCH'}),

  listRequests: (status?: RequestStatusValue) =>
    request<MaintenanceRequest[]>(`/requests${status ? `?status=${status}` : ''}`),

  updateRequestStatus: (id: string, status: RequestStatusValue, note?: string) =>
    request<MaintenanceRequest>(`/requests/${id}/status`, {method: 'PATCH', body: JSON.stringify({status, note})}),

  requestMessages: (id: string) => request<ChatMessage[]>(`/requests/${id}/messages`),

  sendRequestMessage: (id: string, body: string) =>
    request<ChatMessage>(`/requests/${id}/messages`, {method: 'POST', body: JSON.stringify({body})}),

  // ─── Module 8: announcements & door codes ───────────────────────────
  publicAnnouncements: () => request<Announcement[]>('/announcements'),
  adminAnnouncements: () => request<Announcement[]>('/announcements/admin/all'),
  createAnnouncement: (data: AnnouncementInput) =>
    request<Announcement>('/announcements', {method: 'POST', body: JSON.stringify(data)}),
  updateAnnouncement: (id: string, data: Partial<AnnouncementInput>) =>
    request<Announcement>(`/announcements/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),
  deleteAnnouncement: (id: string) =>
    request<void>(`/announcements/${id}`, {method: 'DELETE'}),

  doorCodes: () => request<DoorCode[]>('/door-codes'),
  adminDoorCodes: () => request<DoorCode[]>('/door-codes/admin/all'),
  createDoorCode: (data: DoorCodeInput) =>
    request<DoorCode>('/door-codes', {method: 'POST', body: JSON.stringify(data)}),
  updateDoorCode: (id: string, data: Partial<DoorCodeInput>) =>
    request<DoorCode>(`/door-codes/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),
  deleteDoorCode: (id: string) =>
    request<void>(`/door-codes/${id}`, {method: 'DELETE'}),

  // ─── Module 9: info pages ───────────────────────────────────────────
  publicInfoPages: () => request<InfoPage[]>('/info-pages'),
  publicInfoPage: (slug: string) => request<InfoPage>(`/info-pages/${slug}`),
  adminInfoPages: () => request<InfoPage[]>('/info-pages/admin/all'),
  updateInfoPage: (id: string, data: {title?: string; body?: string}) =>
    request<InfoPage>(`/info-pages/${id}`, {method: 'PATCH', body: JSON.stringify(data)}),

  // ─── Dashboard stats ────────────────────────────────────────────────
  dashboardStats: () => request<DashboardStats>('/stats/dashboard'),

  // ─── Flyer automation (Module 10) ───────────────────────────────────
  publicFlyer: () => request<FlyerData>('/flyer'),
  flyerSettings: () => request<FlyerSettings>('/flyer/settings'),
  updateFlyerSettings: (data: Partial<FlyerSettingsInput>) =>
    request<FlyerSettings>('/flyer/settings', {method: 'PATCH', body: JSON.stringify(data)}),

  // ─── Google integrations (Module 11) ────────────────────────────────
  googleStatus: () => request<GoogleStatus>('/google/status'),
  exportToSheets: () =>
    request<{bookings: number; payments: number; renters: number}>('/exports/sheets', {method: 'POST'}),
  formResponses: () => request<FormResponses>('/google/forms/responses'),
}

export interface ServiceStatus {
  enabled: boolean
  ok: boolean
  message: string
}

export interface GoogleStatus {
  enabled: boolean
  calendar: ServiceStatus
  sheets: ServiceStatus
  drive: ServiceStatus
  forms: ServiceStatus
}

export interface FormResponse {
  id: string
  submittedAt: string
  answers: {question: string; value: string}[]
}
export interface FormResponses {
  title: string
  responses: FormResponse[]
}
